import * as React from 'react';
import { classes, style } from 'typestyle';
import { Messages } from '../../gen-src/Localize';
import { Group, Region, ToolbarItem, ToolbarLayout, VizViewerToolbarProps } from '../PublicTypes';
import { useFitScreenLayoutV2 } from '../helpers/FitScreenHelper';
import { GroupItems } from '../helpers/GroupHelper';
import { hiddenLabelStyles } from '../styles/ButtonStyles';
import { GenericToolbarItem } from '../types/GenericToolbarItem';
import { ToolbarButtonLabelClass } from './Button';
import { GenericToolbar } from './GenericToolbar';
import { TOOLBAR_OVERFLOW_ICON_ID } from './Icons';
import { SeparatorItem } from './Separator';

export const TOOLBAR_LEFT_RIGHT_PADDING = 24;
export const TOOLBAR_TEST_ID = 'viz-viewer-toolbar-items-container';

export const HideLabelsClassName = 'hideLabels';

const ToolbarStyleName = style({
  $debugName: 'toolbar',
  $nest: { [`&.${HideLabelsClassName} .${ToolbarButtonLabelClass}`]: hiddenLabelStyles },
});

export function VizViewerToolbarV2(props: VizViewerToolbarProps & Required<Pick<VizViewerToolbarProps, 'layout'>>): JSX.Element {
  const [leftItems, rightItems] = React.useMemo(
    () => groupAndOrderVizViewerToolbarItems(props.items, props.layout),
    [props.items, props.layout]
  );
  const toolbarRef = React.useRef<HTMLDivElement>(null);
  const [fitLeft, fitRight, hideLabels] = useFitScreenLayoutV2(
    leftItems,
    rightItems,
    props.layout.overflow,
    props.layout.alwaysShow,
    TOOLBAR_LEFT_RIGHT_PADDING,
    toolbarRef
  );

  return (
    <GenericToolbar
      className={classes(ToolbarStyleName, { [HideLabelsClassName]: hideLabels })}
      leftAlignItems={fitLeft}
      rightAlignItems={fitRight}
      leftRightPadding={TOOLBAR_LEFT_RIGHT_PADDING}
      testId={TOOLBAR_TEST_ID}
      id={props.id}
      toolbarRef={toolbarRef}
    />
  );
}

/**
 * Takes a list of VizViewer toolbar items and builds two lists of GenericToolbarItem,
 * one for the left aligned items and one for the right aligned items,
 * based on the group and region info.
 *
 * @param items a list of viz viewer `ToolbarItem`s
 * @returns left aligned items, right aligned items
 */
export const groupAndOrderVizViewerToolbarItems = (items: ToolbarItem[], layout: ToolbarLayout): GenericToolbarItem[][] => {
  const { leftItems, rightItems, overflow } = layout;

  if (!items || items.length === 0) {
    return [[], []];
  }

  // Groups
  const edit: ToolbarItem = { ...GroupItems.edit, nestedItems: [] };
  const data: ToolbarItem = { ...GroupItems.data, nestedItems: [] };
  const discover: ToolbarItem = { ...GroupItems.discover, nestedItems: [] };
  const watchGroup: ToolbarItem = { ...GroupItems.watch, nestedItems: [] };
  const display: ToolbarItem = { ...GroupItems.display, nestedItems: [] };
  const download: ToolbarItem = { ...GroupItems.download, nestedItems: [] };

  const RegionToItemsMap = new Map<Region, ToolbarItem[]>();

  const GroupToItemMap = new Map<Group, ToolbarItem>([
    ['edit', edit],
    ['discover', discover],
    ['data', data],
    ['watch', watchGroup],
    ['download', download],
    ['display', display],
  ]);

  const groupsAdded: Group[] = [];
  items.forEach((item: ToolbarItem) => {
    // Return early if the item is defined in a region not allowed by the layout props
    if (!item.region || (!leftItems.includes(item.region) && !rightItems.includes(item.region))) return;

    if (item.group && item.group !== 'none') {
      const groupItem = GroupToItemMap.get(item.group);
      if (!groupItem) return;

      groupItem.nestedItems!.push(item);

      // We add the group to the first position that a grouped item appears to
      // preserve the monolith position.
      if (groupsAdded.indexOf(item.group) !== -1) {
        // if group has already been added, return
        return;
      } else {
        groupsAdded.push(item.group);
        item = groupItem;
      }
    }

    const regionItems: ToolbarItem[] | undefined = RegionToItemsMap.get(item.region!);
    if (regionItems) {
      regionItems.push(item);
    } else {
      RegionToItemsMap.set(item.region!, [item]);
    }
  });

  const regionsToItems = (regions: Region[], appendSeparatorItem?: boolean): GenericToolbarItem[] =>
    regions.reduce(
      (prevItems, currentRegion) => [
        ...prevItems,
        ...(RegionToItemsMap.get(currentRegion) ?? []).concat(appendSeparatorItem ? SeparatorItem : []),
      ],
      [] as GenericToolbarItem[] // initial value for the reducer
    );

  const leftAlignedItems: ToolbarItem[] = regionsToItems(leftItems, true);
  const rightAlignedItems: ToolbarItem[] = regionsToItems(rightItems, true);

  const overflowMenuItems: GenericToolbarItem[] = regionsToItems(overflow, false);
  const overflowItem = overflowMenuItems.length > 0 ? copyItemsToOverflowItem(overflowMenuItems) : [];

  // Add overflow items to the right side of the toolbar
  return [removeSeparators(leftAlignedItems), removeSeparators(rightAlignedItems).concat(overflowItem)];
};

/**
 * @param items list of toolbar items
 * @returns list of toolbar items that has no separators next to each other
 * and doesn't start or end with a separator
 */
const removeSeparators = (items: GenericToolbarItem[]): GenericToolbarItem[] => {
  const updatedItems = items.reduce(
    (previousItems, currentItem) =>
      previousItems.length > 0 && previousItems[previousItems.length - 1].isSeparator && currentItem.isSeparator
        ? previousItems
        : [...previousItems, currentItem],
    [] as GenericToolbarItem[] // initial value for the reducer
  );

  if (updatedItems.length > 0 && updatedItems[0].isSeparator) {
    updatedItems.shift();
  }
  if (updatedItems.length > 0 && updatedItems[updatedItems.length - 1].isSeparator) {
    updatedItems.pop();
  }

  return updatedItems;
};

/**
 * @param items list of toolbar items
 * @returns toolbar item for an overflow dropdown whose `nestedItems` are created from the input list
 */
const copyItemsToOverflowItem = (items: GenericToolbarItem[]): GenericToolbarItem => {
  const overflowNestedItems: GenericToolbarItem[] = [];

  items
    .filter((item) => item.itemType === 'splitButton' || !item.nestedItems) // first add the top level items
    .forEach((item) => {
      overflowNestedItems.push({ ...item });
    });

  items
    .filter((item) => !!item.nestedItems && item.itemType !== 'splitButton') // then add the grouped items with header
    .forEach((item) => {
      overflowNestedItems.push({ label: item.label, testId: item.testId, isHeader: true } as unknown as GenericToolbarItem);
      item.nestedItems!.forEach((item) => {
        overflowNestedItems.push({ ...item });
      });
    });

  return {
    iconId: TOOLBAR_OVERFLOW_ICON_ID,
    id: TOOLBAR_OVERFLOW_ICON_ID,
    isOverflow: true,
    label: Messages.moreOptions(),
    nestedItems: overflowNestedItems,
    testId: TOOLBAR_OVERFLOW_ICON_ID,
  } as GenericToolbarItem;
};
